
import React, { useState, useCallback } from 'react';
import { Grid, BuildingType, CityStats, GameStatus } from '../types';
import { UndergroundLayer } from '../types/underground';
import { BUILDINGS } from '../constants';
import { playUiSfx } from '../utils/uiAudio';
import { NewsItem } from '../types';
import { gameStore } from '../stores/gameStore';
import { validateBuildingPlacement } from '../utils/layerPlacementValidation';
import { architectureFeatures } from '../src/infrastructure/config/features';
import {
    resolvePlaceBuilding,
    resolveUpgradeBuilding,
    resolveDeleteBuilding,
} from '../src/adapters/ui/hooks/usePlaceBuilding';

const refreshGridConnectivity = () => {
  queueMicrotask(() => gameStore.getState().refreshConnectivity());
};

interface GridActionsProps {
    grid: Grid;
    setGrid: React.Dispatch<React.SetStateAction<Grid>>;
    stats: CityStats;
    setStats: React.Dispatch<React.SetStateAction<CityStats>>;
    addNewsItem: (item: NewsItem) => void;
    gameStarted: boolean;
    gameStatus: GameStatus;
    activeLayer?: UndergroundLayer;
}

export const useGridActions = ({
    grid,
    setGrid,
    stats,
    setStats,
    addNewsItem,
    gameStarted,
    gameStatus,
    activeLayer = UndergroundLayer.Surface,
}: GridActionsProps) => {
    const [selectedTool, setSelectedTool] = useState<BuildingType>(BuildingType.None);
    const [selectedTile, setSelectedTile] = useState<{x: number, y: number} | null>(null);

    const getPlacementError = useCallback((x: number, y: number, tool: BuildingType): string | null => {
        if (tool === BuildingType.None) return null;
        const validation = validateBuildingPlacement(grid, activeLayer, x, y, tool);
        return validation.isValid ? null : validation.message ?? 'Placement blocked.';
    }, [grid, activeLayer]);

    const handleTileClick = useCallback((x: number, y: number) => {
        if (!gameStarted || (gameStatus !== 'playing' && gameStatus !== 'scenario_won')) return;

        const currentTile = grid[y][x];
        const buildingConfig = BUILDINGS[selectedTool];

        if (selectedTool === BuildingType.None) {
            if (currentTile.buildingType !== BuildingType.None) setSelectedTile({x, y});
            else setSelectedTile(null);
            return;
        }

        if (currentTile.buildingType === BuildingType.None) {
            const placementError = getPlacementError(x, y, selectedTool);
            if (placementError) {
                addNewsItem({
                    id: Date.now().toString(),
                    text: placementError,
                    type: 'negative',
                });
                playUiSfx('error');
                return;
            }

            if (architectureFeatures.usePlaceBuildingUseCase) {
                const result = resolvePlaceBuilding({
                    grid,
                    stats,
                    x,
                    y,
                    buildingType: selectedTool,
                });
                if (result.ok) {
                    setStats(result.stats);
                    setGrid(result.grid);
                    playUiSfx('build');
                } else {
                    addNewsItem({
                        id: Date.now().toString(),
                        text: result.error ?? 'Placement blocked.',
                        type: 'negative',
                    });
                    playUiSfx('error');
                }
                return;
            }

            if (stats.budget >= buildingConfig.cost) {
                setStats(prev => ({ ...prev, budget: prev.budget - buildingConfig.cost }));
                setGrid(prev => {
                    const newGrid = prev.map(row => [...row]);
                    newGrid[y][x] = { ...currentTile, buildingType: selectedTool, level: 1, isConnected: false };
                    return newGrid;
                });
                refreshGridConnectivity();
                playUiSfx('build');
            } else {
                addNewsItem({id: Date.now().toString(), text: `Insufficient funds.`, type: 'negative'});
                playUiSfx('error');
            }
        } else {
            setSelectedTile({x, y});
            playUiSfx('click');
        }
    }, [selectedTool, addNewsItem, gameStarted, gameStatus, grid, stats.budget, setGrid, setStats, getPlacementError]);

    const handleUpgrade = useCallback((hasJumpGate: boolean) => {
        if (!selectedTile) return;

        if (architectureFeatures.usePlaceBuildingUseCase) {
            const result = resolveUpgradeBuilding({
                grid,
                stats,
                x: selectedTile.x,
                y: selectedTile.y,
                hasJumpGate,
            });
            if (result.ok) {
                setStats(result.stats);
                setGrid(result.grid);
                playUiSfx('build');
            } else if (result.error) {
                addNewsItem({ id: Date.now().toString(), text: result.error, type: 'negative' });
                playUiSfx('error');
            }
            return;
        }

        if (!hasJumpGate) {
            addNewsItem({ id: Date.now().toString(), text: "ACCESS DENIED: Upgrades require Jump Gate.", type: 'negative' });
            playUiSfx('error');
            return;
        }

        const tile = grid[selectedTile.y][selectedTile.x];

        const config = BUILDINGS[tile.buildingType];
        const level = tile.level || 1;
        const cost = Math.floor(config.cost * level * 1.2);

        if (level >= 5) return;

        if (stats.budget >= cost) {
            setStats(prev => ({ ...prev, budget: prev.budget - cost }));
            setGrid(prev => {
                const newGrid = prev.map(row => [...row]);
                if (selectedTile) newGrid[selectedTile.y][selectedTile.x] = { ...newGrid[selectedTile.y][selectedTile.x], level: level + 1 };
                return newGrid;
            });
            refreshGridConnectivity();
            playUiSfx('build');
        } else {
            addNewsItem({id: Date.now().toString(), text: `Insufficient funds for upgrade.`, type: 'negative'});
            playUiSfx('error');
        }
    }, [grid, selectedTile, stats, setGrid, setStats, addNewsItem]);

    const handleDelete = useCallback(() => {
        if (!selectedTile) return;

        if (architectureFeatures.usePlaceBuildingUseCase) {
            const result = resolveDeleteBuilding({
                grid,
                stats,
                x: selectedTile.x,
                y: selectedTile.y,
            });
            if (result.ok) {
                setGrid(result.grid);
                setSelectedTile(null);
                playUiSfx('destroy');
            } else if (result.error) {
                addNewsItem({ id: Date.now().toString(), text: result.error, type: 'negative' });
                playUiSfx('error');
            }
            return;
        }

        const tile = grid[selectedTile.y][selectedTile.x];

        if (tile.buildingType === BuildingType.Core) {
            addNewsItem({id: Date.now().toString(), text: `Cannot dismantle The HUB.`, type: 'negative'});
            playUiSfx('error');
            return;
        }

        setGrid(prev => {
            const newGrid = prev.map(row => [...row]);
            newGrid[selectedTile.y][selectedTile.x] = { ...newGrid[selectedTile.y][selectedTile.x], buildingType: BuildingType.None, level: 1 };
            return newGrid;
        });
        refreshGridConnectivity();
        setSelectedTile(null);
        playUiSfx('destroy');
    }, [grid, selectedTile, stats, setGrid, addNewsItem]);

    return {
        selectedTool,
        setSelectedTool,
        selectedTile,
        setSelectedTile,
        handleTileClick,
        handleUpgrade,
        handleDelete,
        getPlacementError,
    };
};